import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types';
import apiService, {
  logAuthTokenExpiry,
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_MESSAGE_KEY,
} from '../services/api';
import { HOTQUU_ACCESS_DENIED_MESSAGE, userHasHotQuuAccess } from '../utils/hotQuuAccess';

const SESSION_STARTED_KEY = 'hotquuSessionStartedAt';

function clearStoredSession(): void {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userData');
  localStorage.removeItem('selectedStation');
  localStorage.removeItem('userStations');
  localStorage.removeItem(SESSION_STARTED_KEY);
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  hasSelectedStation: boolean;
  setStationSelected: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasSelectedStation, setHasSelectedStation] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      const selectedStation = localStorage.getItem('selectedStation');

      if (token && userData) {
        try {
          const parsed = JSON.parse(userData) as User;
          if (!(await apiService.ensureValidSession())) {
            clearStoredSession();
            sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, SESSION_EXPIRED_MESSAGE);
            setUser(null);
            setHasSelectedStation(false);
            setLoading(false);
            return;
          }
          setUser(parsed);
          if (selectedStation) {
            setHasSelectedStation(true);
          }
          logAuthTokenExpiry('restored session');
        } catch (error) {
          console.error('Error parsing user data from localStorage:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('selectedStation');
          localStorage.removeItem('userStations');
          localStorage.removeItem(SESSION_STARTED_KEY);
          setUser(null);
          setHasSelectedStation(false);
        }
      } else if (token && !userData) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('selectedStation');
        localStorage.removeItem('userStations');
        localStorage.removeItem(SESSION_STARTED_KEY);
        setUser(null);
        setHasSelectedStation(false);
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const { user, token } = await apiService.login({ username, password });

      if (!userHasHotQuuAccess(user)) {
        throw new Error(HOTQUU_ACCESS_DENIED_MESSAGE);
      }

      localStorage.removeItem('selectedStation');
      localStorage.removeItem('userStations');
      localStorage.setItem('authToken', token);
      localStorage.setItem('userData', JSON.stringify(user));
      localStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
      logAuthTokenExpiry('after login', token);

      setUser(user);
      setHasSelectedStation(false);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('selectedStation');
      localStorage.removeItem('userStations');
      localStorage.removeItem(SESSION_STARTED_KEY);
      setUser(null);
      setHasSelectedStation(false);
    }
  }, []);

  const setStationSelected = () => {
    setHasSelectedStation(true);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    hasSelectedStation,
    setStationSelected,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
